/**
 * Campaign edit history — bounded undo/redo over CAMPAIGN snapshots.
 *
 * The store treats every object as immutable, so a snapshot is one cheap
 * REFERENCE to the previous campaign root: maps, arrays and sheets stay
 * shared with every later state (a deep clone of each map per keystroke
 * would be the real cost — this design pays nothing per edit).
 *
 * Design rules:
 *
 *  · WHAT is captured — `state.campaign` and nothing else. UI state (route,
 *    selection, locale, toasts, palette) never enters the stacks, and the
 *    store's undo/redo actions write ONLY `campaign`, so pressing Ctrl+Z
 *    can never jump the GM to another view or reopen a dialog.
 *  · BOUND — HISTORY_LIMIT (50) entries per side; the oldest entry drops
 *    off the bottom, so memory is capped no matter how long the session.
 *  · COALESCING — edits less than COALESCE_WINDOW_MS (600 ms) apart belong
 *    to one burst (typing a name sends one call per keystroke) and collapse
 *    into the single entry recorded when the burst started: one Ctrl+Z then
 *    reverts the whole burst, not a single letter.
 *  · REDO BRANCH — any new edit clears the redo stack (standard undo
 *    model); an edit right after an undo is never coalesced into the entry
 *    the undo just consumed.
 *  · RESET across documents — creating, importing or restoring a campaign
 *    REPLACES the document (store.ts createCampaign / replaceCampaign /
 *    clearCampaign wipe this module): the GM can never undo back into a
 *    previous campaign.
 *  · AUTOSAVE — undo/redo swaps `campaign` like any other edit, so the
 *    existing debounced autosave in persistence/storage.ts persists the
 *    undone state through the ONE storage path; nothing here touches
 *    storage, and the save-status writes it makes are UI state (never
 *    recorded).
 *
 * COMBAT STATE IS COVERED — deliberately. The tracker's live state (round,
 * active turn, per-participant HP, initiative, conditions) lives ON the
 * campaign and every combat write already funnels through the same mutating
 * action (store.updateCampaign). Because snapshots are WHOLE campaigns,
 * carving combat out would not actually protect it: an un-recorded combat
 * edit still gets embedded in the NEXT recorded snapshot and would then be
 * silently reverted together with it — the "round rewinds mid-fight without
 * me noticing" hazard, only hidden better. Full coverage keeps undo LINEAR:
 * each undo restores exactly the state immediately before ONE edit, so a
 * mis-clicked damage number, a wrong condition toggle or a double-clicked
 * "next turn" are all recoverable, and the round counter only moves on an
 * undo whose most recent edit WAS that turn step — visible in the tracker,
 * never silent. Accepted overlap: a damage click and a turn advance within
 * the same 600 ms window coalesce into one entry (the specified window).
 */
import { useSyncExternalStore } from 'react';
import type { Campaign } from '@/domain/campaign';

/** Hard cap per side — the oldest entry is dropped when this is reached. */
export const HISTORY_LIMIT = 50;

/** Edits closer together than this collapse into ONE history entry. */
export const COALESCE_WINDOW_MS = 600;

export interface HistoryStatus {
  /** True while at least one entry can be undone (the Undo button lights up). */
  canUndo: boolean;
  /** True while at least one undone edit waits on the redo branch. */
  canRedo: boolean;
}

let undoStack: Campaign[] = [];
let redoStack: Campaign[] = [];
/** Wall-clock of the last recorded edit — drives the coalescing window. */
let lastRecordedAt = Number.NEGATIVE_INFINITY;
/** What happened last: only a run of EDITS may coalesce together. */
let lastOperation: 'edit' | 'step' | null = null;
let coalesceWindowMs = COALESCE_WINDOW_MS;

let status: HistoryStatus = { canUndo: false, canRedo: false };
type Listener = () => void;
const listeners = new Set<Listener>();

/** Publish only on a real button-state change (no re-render per keystroke). */
function notify(): void {
  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;
  if (canUndo === status.canUndo && canRedo === status.canRedo) return;
  status = { canUndo, canRedo };
  for (const listener of listeners) listener();
}

function pushBounded(stack: Campaign[], snapshot: Campaign): void {
  if (stack.length >= HISTORY_LIMIT) stack.shift(); // drop the oldest
  stack.push(snapshot);
}

/**
 * Called by the store's ONE mutating action with the campaign as it was
 * JUST BEFORE an edit. A burst inside the coalescing window is swallowed
 * (the entry for the burst's first edit is already on the stack). Clears
 * the redo branch — a new edit after an undo abandons it, always.
 */
export function recordCampaignEdit(previous: Campaign): void {
  const now = Date.now();
  const insideBurst =
    lastOperation === 'edit' && now - lastRecordedAt < coalesceWindowMs;
  if (redoStack.length > 0) redoStack = [];
  if (!insideBurst) pushBounded(undoStack, previous);
  lastRecordedAt = now;
  lastOperation = 'edit';
  notify();
}

/**
 * Undo: hand back the previous snapshot and park the state being left on
 * the redo branch. Returns null when there is nothing to undo.
 */
export function takeUndo(current: Campaign): Campaign | null {
  const previous = undoStack.pop();
  if (previous === undefined) return null;
  pushBounded(redoStack, current);
  lastOperation = 'step'; // the next edit must start a fresh entry
  notify();
  return previous;
}

/** Redo: the inverse of takeUndo; null when the redo branch is empty. */
export function takeRedo(current: Campaign): Campaign | null {
  const next = redoStack.pop();
  if (next === undefined) return null;
  pushBounded(undoStack, current);
  lastOperation = 'step';
  notify();
  return next;
}

/**
 * Wipe both branches — called whenever the document is REPLACED (new /
 * imported / restored campaign, test reset): undo must never cross back
 * into a previous campaign.
 */
export function resetCampaignHistory(): void {
  undoStack = [];
  redoStack = [];
  lastRecordedAt = Number.NEGATIVE_INFINITY;
  lastOperation = null;
  notify();
}

/** Current undo depth (tools + tests; the UI only needs `canUndo`). */
export function historyDepth(): number {
  return undoStack.length;
}

export function getHistoryStatus(): HistoryStatus {
  return status;
}

export function subscribeHistory(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** React binding for the chrome's Undo/Redo pair (stable snapshot identity). */
export function useHistoryStatus(): HistoryStatus {
  return useSyncExternalStore(subscribeHistory, getHistoryStatus, getHistoryStatus);
}

/**
 * Test hook for tools/smoke-history.ts: shrink (or grow) the coalescing
 * window so a gate can produce N distinct entries without waiting 600 ms
 * between edits. Returns a restore function. Not used by the UI.
 */
export function __setCoalesceWindow(ms: number): () => void {
  const previous = coalesceWindowMs;
  coalesceWindowMs = ms;
  return () => {
    coalesceWindowMs = previous;
  };
}
